import { Component, OnInit, ViewChild } from '@angular/core';
import { HttpBackend, HttpClient } from '@angular/common/http';
import Utils from '@iv/shared/utils';
import { Instrument } from '@iv/model/instrument';

@Component({
  selector: 'price-historical-implied-volatility',
  templateUrl: './price-historical-implied-volatility.component.html',
  styleUrls: ['./price-historical-implied-volatility.component.scss']
})
export class PriceHistoricalImpliedVolatilityComponent implements OnInit{
  @ViewChild('autocomplete') autocomplete;
  instrument: Instrument;
  instruments: Instrument[] = [];

  chartCommonSettings: any = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {display: false},
      tooltip: {intersect: false}
    },
    layout: {
      padding: {
        left: 16,
        right: 26,
        bottom: 16
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        position: 'left',
      },
      x: {
        beginAtZero: true,
        ticks: {
          maxTicksLimit: 12,
          callback: function(val) {
            const date = this.getLabelForValue(val);
            const month = date.substring(4, 7);
            return month === 'Jan' ? 'Jan ' + date.substring(11) : month;
          },
        }
      }
    }
  };

  tab: string = 'price&volume';
  private http: HttpClient;

  constructor(private httpBackend: HttpBackend) { }

  ngOnInit(): void {
    this.http = new HttpClient(this.httpBackend);

    this.http.get('/widget/instruments').subscribe((instruments: Instrument[]) => {
      this.instruments = instruments.map(instrument => Object.assign(new Instrument(), instrument));
      this.instrument = this.instruments.find(i => i.symbol === 'SPY');
      this.getData(this.instrument.symbol);
    });
  }

  getStockType(item: any) {
    return Utils.getStockType(item);
  }

  onAutocompleteItemClick(e?: any) {
    this.instrument = e.itemData;
    this.resetAutocomplete();
    this.getData(this.instrument.symbol);
  }
  private resetAutocomplete() {
    if (this.autocomplete) {
      this.autocomplete.instance.reset();
    }
  }

  ivxHv: any;
  volatilityScale: any;
  latestIvIndex: any;
  weekIvIndex: any;
  yearBounds: any;
  correlationToday: any;
  correlationWeekly: any;
  correlationBounds: any;
  stockVolaInfo: any;

  getData(symbol: string) {
    this.ivxHv = null;
    this.volatilityScale = null;
    this.latestIvIndex = null;
    this.weekIvIndex = null;
    this.yearBounds = null;
    this.correlationToday = null;
    this.correlationWeekly = null;
    this.correlationBounds = null;
    this.stockVolaInfo = null;

    const Chart = window['Chart'];

    this.http.get('/widget/ivx-hv?symbol=' + symbol).subscribe((data: any) => {
      this.ivxHv = data;

      setTimeout(() => {
        let min = data.reduce((acc, cur) => ((cur.hv20 < acc.hv20 && cur.hv20 < acc.ivx30 || cur.ivx30 < acc.hv20 && cur.ivx30 < acc.ivx30) ? cur : acc));
        let max = data.reduce((acc, cur) => ((cur.hv20 > acc.hv20 && cur.hv20 > acc.ivx30 || cur.ivx30 > acc.hv20 && cur.ivx30 > acc.ivx30) ? cur : acc));
        min = min.hv20 < min.ivx30 ? min.hv20 : min.ivx30;
        max = max.hv20 > max.ivx30 ? max.hv20 : max.ivx30;

        const ctx2 = document.getElementById('historicalImpliedChart');
        new Chart(ctx2, {
          type: 'line',
          data: {
            datasets: [
              {
                data: data.map(p => ({x: (new Date(p.tdate)).toDateString(), y: p.hv20})),
                borderWidth: 4,
                borderColor: '#F89770',
                pointRadius: 0
              },
              {
                data: data.map(p => ({x: (new Date(p.tdate)).toDateString(), y: p.ivx30})),
                borderWidth: 4,
                borderColor: '#4DAA83',
                pointRadius: 0
              },
            ]
          },
          options: {
            ...this.chartCommonSettings,
            scales: {
              ...this.chartCommonSettings.scales,
              y: {
                min: Math.round(min - 0.05 * (max - min)),
                max: Math.round(max + 0.05 * (max - min)),
              },
            }
          }
        });
      }, 10);
    });
    this.http.get('/widget/volatility-scale?symbol=' + symbol).subscribe((data: any) => {
      this.volatilityScale = data;

      setTimeout(() => {
        const ctx1 = document.getElementById('priceVolumeChart');
        const minPrice = data.reduce((acc, cur) => (acc.price > cur.price ? cur : acc)).price;
        const maxPrice = data.reduce((acc, cur) => (acc.price < cur.price ? cur : acc)).price;
        const maxVolume = Math.round(data.reduce((acc, cur) => (acc.volume < cur.volume ? cur : acc)).volume);
        new Chart(ctx1, {
          type: 'bar',
          data: {
            datasets: [
              {
                data: data.map(p => ({x: (new Date(p.tdate)).toDateString(), y: p.price})),
                borderWidth: 4,
                type: 'line',
                borderColor: '#4DAA83',
                pointRadius: 0,
                yAxisID: 'y',
              },
              {
                data: data.map(p => ({x: (new Date(p.tdate)).toDateString(), y: p.volume})),
                borderWidth: 0,
                backgroundColor: '#F89770',
                yAxisID: 'y1',
              },
            ]
          },
          options: {
            ...this.chartCommonSettings,
            scales: {
              ...this.chartCommonSettings.scales,
              y: {
                position: 'left',
                min: Math.round(minPrice - 0.05 * (maxPrice - minPrice)),
                max: Math.round(maxPrice + 0.05 * (maxPrice - minPrice)),
              },
              y1: {
                position: 'right',
                max: maxVolume * 3,
                ticks: {
                  callback: function(val) {
                    const value = this.getLabelForValue(val);
                    if (value > (maxVolume + 0.1 * maxVolume)) {
                      return null;
                    }
                    return value === '0' ? value : value + 'M';
                  },
                }
              }
            },
          }
        });
      }, 10);
    });
    this.http.get('/widget/latest-iv-index?symbol=' + symbol).subscribe((data: any) => {this.latestIvIndex = data;});
    this.http.get('/widget/week-iv-index?symbol=' + symbol).subscribe((data: any) => {this.weekIvIndex = data;});
    this.http.get('/widget/year-bounds?symbol=' + symbol).subscribe((data: any) => {this.yearBounds = data;});
    this.http.get('/widget/correlation-beta/today?symbol=' + symbol).subscribe((data: any) => {this.correlationToday = data;});
    this.http.get('/widget/correlation-beta/today/weekly?symbol=' + symbol).subscribe((data: any) => {this.correlationWeekly = data;});
    this.http.get('/widget/correlation-beta/today/bounds?symbol=' + symbol).subscribe((data: any) => {this.correlationBounds = data;});
    this.http.get('/widget/stock-vola-info?symbol=' + symbol).subscribe((data: any) => {this.stockVolaInfo = data;});
  }

  formatPercent(value) {
    return (value * 100).toFixed(2) + '%';
  }
  formatDate(date) {
    return (new Date(date)).toDateString().substring(4, 10)
  }

  isDataLoaded() {
    return this.latestIvIndex &&
      this.weekIvIndex &&
      this.yearBounds &&
      this.correlationToday &&
      this.correlationWeekly &&
      this.correlationBounds &&
      this.stockVolaInfo;
  }
}
